import { useCallback, useState } from 'react';

import { ShinroRelease } from 'components/pages/shinro/types';

const STORAGE_KEYS = {
  BADGE: 'parents_web_unread_shinro_badge',
  POPUP: 'parents_web_unread_shinro_popup',
  SURVEY_POPUP: 'parents_web_unread_shinro_survey_popup',
} as const;

type ReleaseKey = keyof typeof ShinroRelease.releases;

/**
 * 進路情報の既読状態を管理するカスタムフック
 *
 * @returns {Object} 既読状態の管理に必要な関数と状態
 * @property {boolean} isBadgeRead - 進路情報未読バッジの既読状態
 * @property {boolean} isPopupRead - 進路情報ポップアップの既読状態
 * @property {boolean} isSurveyPopupRead - アンケートポップアップの既読状態
 * @property {Function} markBadgeAsRead - 進路情報未読バッジを既読にする
 * @property {Function} markPopupAsRead - 進路情報ポップアップを既読にする
 * @property {Function} markSurveyPopupAsRead - アンケートポップアップを既読にする
 */

export const useShinroReadStatus = () => {
  const currentRelease = ShinroRelease.current as ReleaseKey;
  const isSurveyWeek = currentRelease === 'survey';
  const releases = ShinroRelease.releases;
  const releaseInfo = (key: ReleaseKey) => releases[key];
  const isFeedbackEndWeek = releaseInfo(currentRelease)?.lasted ?? false;

  const getStoredValue = (key: string) => localStorage.getItem(key);

  const [isBadgeRead, setIsBadgeRead] = useState(() => {
    if (isFeedbackEndWeek) return true;
    const storedValue = getStoredValue(STORAGE_KEYS.BADGE) as ReleaseKey;
    if (!storedValue) return false;
    if (isSurveyWeek) {
      return releaseInfo(storedValue)?.lasted || storedValue === 'survey';
    }
    return storedValue === currentRelease;
  });

  const [isPopupRead, setIsPopupRead] = useState(() => {
    if (isFeedbackEndWeek) return true;
    const storedValue = getStoredValue(STORAGE_KEYS.POPUP) as ReleaseKey;
    if (!storedValue) return false;
    if (isSurveyWeek) {
      return releaseInfo(storedValue)?.lasted || storedValue === 'survey';
    }
    return storedValue === currentRelease;
  });

  const [isSurveyPopupRead, setIsSurveyPopupRead] = useState(() => {
    const storedValue = getStoredValue(STORAGE_KEYS.SURVEY_POPUP) as ReleaseKey;
    return releaseInfo(storedValue)?.lasted || storedValue === 'survey';
  });

  const setStoredValue = useCallback((key: string, value: string) => {
    localStorage.setItem(key, value);
  }, []);

  const markBadgeAsRead = useCallback(() => {
    if (isFeedbackEndWeek) return;
    setStoredValue(STORAGE_KEYS.BADGE, currentRelease);
    setIsBadgeRead(true);
  }, [currentRelease, setStoredValue, isFeedbackEndWeek]);

  const markPopupAsRead = useCallback(() => {
    if (isFeedbackEndWeek) return;
    setStoredValue(STORAGE_KEYS.POPUP, currentRelease);
    setIsPopupRead(true);
  }, [currentRelease, setStoredValue, isFeedbackEndWeek]);

  const markSurveyPopupAsRead = useCallback(() => {
    setStoredValue(STORAGE_KEYS.SURVEY_POPUP, 'survey');
    setIsSurveyPopupRead(true);
  }, [setStoredValue]);

  return {
    isBadgeRead,
    isPopupRead,
    isSurveyPopupRead,
    markBadgeAsRead,
    markPopupAsRead,
    markSurveyPopupAsRead,
  };
};
